import React, { useEffect, useState } from 'react';
import { Button, Paper, TextField, Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

import { PageType } from '../../../common/constants';
import { EmailIsValid, IfFalseThrowError, IsAdminOrContributor, IsEmpty, IsValidUrl } from '../../../common/checks';
import ApiService from '../../../services/apiService';
import pageAction from '../../../actions/page';
import Loading from '../../../components/Loading';
import AddressSearch from '../../../components/AddressSearch';
import MText from '../../../components/Common/MText';
import { containerHeaderTextStyle } from '../../../styles/common';
import Colours from '../../../styles/colours';
import { Trim } from '../../../common/util';
import MButton from '../../../components/MButton';
import MTextField from '../../../components/MTextField';
import SocialLinkInput from './SocialLinkInput';

const TEXT_SIZE = 13;

const Page = (props) => {
  const [loading, setLoading] = useState(false);
  const [openAddress, setOpenAddress] = useState(false);
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [website, setWebsite] = useState('');
  const [addressDisplay, setAddressDisplay] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [facebookLink, setFacebookLink] = useState(null);
  const [instagramLink, setInstagramLink] = useState(null);
  const [tiktokLink, setTiktokLink] = useState(null);
  const [contactNo, setContactNo] = useState('');
  const [contactOthers, setContactOthers] = useState('');

  const disabled = !IsAdminOrContributor(props.selectedPage.staffType);

  const closeAddress = () => setOpenAddress(false);

  const fetchPage = async () => {
    try {
      setLoading(true);

      let page = await ApiService.getPageById(props.selectedPage.id);
      page = { ...page, plan: props.selectedPage.plan };
      setDescription(page.description || '');
      setType(page.type || '');
      setWebsite(page.website || '');
      setAddress(page.address || '');
      setPostalCode(page.postalCode || '');
      setLat(page.lat);
      setLng(page.lng);
      setContactEmail(page.contactEmail || '');
      setContactNo(page.contactNo || '');
      setContactOthers(page.contactOthers || '');
      setFacebookLink(page.facebook || null);
      setInstagramLink(page.instagram || null);
      setTiktokLink(page.tiktok || null);

      props.dispatchPageUpdate(page);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const populateAddress = (result) => {
    if (!IsEmpty(result)) {
      setAddress(result.address);
      setPostalCode(result.postalCode);
      setLat(result.lat || null);
      setLng(result.lng || null);
    } else {
      setAddress('');
      setPostalCode('');
      setLat(result.lat || null);
      setLng(result.lng || null);
    }
  };

  const validate = (payload) => {
    // eslint-disable-next-line no-shadow
    let { contactEmail, contactOthers, contactNo, facebook, instagram, tiktok } = payload;

    const fldContactEmail = Trim(contactEmail);
    const fldContactOthers = Trim(contactOthers);
    const fldContactNo = Trim(contactNo);

    setContactEmail(fldContactEmail);
    setContactOthers(fldContactOthers);
    setContactNo(fldContactNo);

    if (!IsEmpty(fldContactEmail)) {
      IfFalseThrowError(EmailIsValid(fldContactEmail), 'Email invalid format.');
    }

    if (!IsEmpty(facebook) && !IsEmpty(facebook.replaceAll('\n', ''))) {
      facebook = Trim(facebook);
      facebook.split(/\s+/).forEach((link) => {
        IfFalseThrowError(IsValidUrl(link), 'Facebook link is invalid');
      });
    }

    if (!IsEmpty(instagram) && !IsEmpty(instagram.replaceAll('\n', ''))) {
      instagram = Trim(instagram);
      instagram.split(/\s+/).forEach((link) => {
        IfFalseThrowError(IsValidUrl(link), 'Instagram link is invalid');
      });
    }

    if (!IsEmpty(tiktok) && !IsEmpty(tiktok.replaceAll('\n', ''))) {
      tiktok = Trim(tiktok);
      tiktok.split(/\s+/).forEach((link) => {
        IfFalseThrowError(IsValidUrl(link), 'Tiktok link is invalid');
      });
    }

    // IfTrueThrowError(
    //   (IsEmpty(fldContactEmail) && IsEmpty(fldContactOthers) && IsEmpty(fldContactNo)),
    //   'Please provide at least one method for customers to contact you.',
    // );

    return { ...payload, contactEmail: fldContactEmail, contactOthers: fldContactOthers, contactNo: fldContactNo };
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    try {
      let payload = {
        description,
        website,
        contactEmail,
        lat,
        lng,
        contactOthers,
        contactNo,
        facebook:  Trim(facebookLink, false, true, true),
        instagram: Trim(instagramLink, false, true, true),
        tiktok:    Trim(tiktokLink, false, true, true),
      };

      payload = validate(payload);
      setLoading(true);

      if (type === PageType.Mall) {
        payload = { ...payload, address, postalCode, lat, lng };
      }
      await ApiService.updatePage(props.selectedPage.id, payload);
      toast.success('Information updated.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
  }, []);

  useEffect(() => {
    if (!IsEmpty(address)) {
      setAddressDisplay(`${address}\nSingapore ${postalCode}`);
    } else {
      setAddressDisplay('');
    }
  }, [address, postalCode]);

  return (
    <>
      {loading && <Loading />}
      {openAddress && <AddressSearch onCancel={closeAddress} onDone={(e) => populateAddress(e)} />}
      <form
        className="container-fluid"
        onSubmit={(e) => onSubmit(e)}
        autoComplete="off"
      >
        <div className="mt-4 ml-2 mb-2">
          <MText text="Information" size={containerHeaderTextStyle.fontSize} bold />
        </div>
        <div className="row ">
          <div className="col-12 col-md-10 col-lg-8 my-3">
            <Paper className="p-4">
              <div className="mb-4">
                <MText text="General" semibold className="mb-1" />
                <MText
                  variant="body1"
                  colour={Colours.Gray}
                  text="1. Website"
                  size={TEXT_SIZE}
                />

                <TextField
                  className="mt-2"
                  id="standard-multiline-flexible"
                  multiline
                  maxRows={4}
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  variant="outlined"
                  size="small"
                  inputProps={{
                    style: {
                      fontSize: 12,
                    },
                  }}
                  fullWidth
                />
              </div>

              <div className="mb-4">
                <div className="d-flex flex-row">
                  <MText
                    variant="body1"
                    colour={Colours.Gray}
                    size={TEXT_SIZE}
                    text="2. Address"
                  />
                  {
                    (type === PageType.Mall)
                    && (
                      <Button
                        className="ml-2"
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => { setOpenAddress(true); }}
                      >
                        <MText variant="caption" text="search" colour={Colours.Primary} />
                      </Button>
                    )
                  }
                </div>

                <TextField
                  id="standard-multiline-flexible"
                  className="mt-2"
                  multiline
                  maxRows={4}
                  value={addressDisplay}
                  variant="outlined"
                  size="small"
                  inputProps={{
                    style: {
                      fontSize: 12,
                    },
                  }}
                  placeholder={(type !== PageType.Mall) ? '(Not Applicable)' : ''}
                  fullWidth
                  disabled
                />
              </div>

              <div className="mb-2">
                <MText
                  variant="body1"
                  colour={Colours.Gray}
                  style={{ marginBottom: 10 }}
                  size={TEXT_SIZE}
                  text="3. Description  (max. 1000 characters)"
                />
                <MTextField
                  value={description}
                  onChange={(value) => setDescription(value)}
                  className="w-100"
                  multiLine
                  minRows={4}
                  maxRows={8}
                  maxCharacters={1000}
                  showCharactersCount
                />
              </div>

              <MText text="Social Networks (optional)" semibold />
              <MText
                text="(For multiple social network links, enter each link in a new line)"
                className="mb-2"
              />

              <div className="mb-4">
                <SocialLinkInput
                  title="4. Facebook"
                  link={facebookLink}
                  onChange={(value) => setFacebookLink(value)}
                />
              </div>

              <div className="mb-4">
                <SocialLinkInput
                  title="5. Instagram"
                  link={instagramLink}
                  onChange={(value) => setInstagramLink(value)}
                />
              </div>

              <div className="mb-4">
                <SocialLinkInput
                  title="6. Tiktok"
                  link={tiktokLink}
                  onChange={(value) => setTiktokLink(value)}
                />
              </div>

              <div className="row">
                {/* <div className="col-md-12 d-flex flex-column">
                  <MText variant="body1" colour={Colours.Gray} size={TEXT_SIZE}>
                    4. How should customers contact you
                    <sup style={{ color: Colours.Red }}>*</sup>
                    {' '}
                  </MText>
                  <MText className="ml-2" text="(provide at least 1 method)" size={10} colour={Colours.Gray} />
                </div>

                <div className="col-md-12 mt-1 pl-5">
                  <div className="mb-4">
                    <MText
                      variant="body2"
                      colour={Colours.Gray}
                      size={12}
                      text="Email"
                    />

                    <TextField
                      className="mt-2"
                      id="standard-multiline-flexible"
                      multiline
                      maxRows={4}
                      value={contactEmail}
                      onChange={(e) => setContactEmail(Trim(e.target.value, true))}
                      onPaste={(e) => {
                        e.preventDefault();
                        const paste = e.clipboardData.getData('text');
                        setContactEmail(Trim(paste, true));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === ' ') {
                          e.preventDefault(); // Prevent space from being typed
                        }
                      }}
                      variant="outlined"
                      size="small"
                      inputProps={{
                        style: {
                          fontSize: 12,
                        },
                      }}
                      fullWidth
                    />
                  </div>

                  <div className="mb-4">
                    <MText
                      variant="body2"
                      colour={Colours.Gray}
                      size={12}
                      text="Phone Number"
                    />

                    <TextField
                      className="mt-2"
                      id="standard-multiline-flexible"
                      multiline
                      type="tel"
                      maxRows={4}
                      value={contactNo}
                      onChange={(e) => setContactNo(e.target.value)}
                      variant="outlined"
                      size="small"
                      inputProps={{
                        style: {
                          fontSize: 12,
                        },
                      }}
                      fullWidth
                    />
                  </div>

                  <div className="mb-5">
                    <MText
                      variant="body2"
                      colour={Colours.Gray}
                      size={12}
                      text="Others"
                    />

                    <TextField
                      className="mt-2"
                      id="standard-multiline-flexible"
                      multiline
                      maxRows={4}
                      value={contactOthers}
                      onChange={(e) => setContactOthers(e.target.value)}
                      variant="outlined"
                      size="small"
                      inputProps={{
                        style: {
                          fontSize: 12,
                        },
                      }}
                      fullWidth
                    />
                  </div>
                </div> */}

                <div className="col-12 d-flex flex-row justify-content-center">
                  <div className="col-12 col-md-6 col-lg-3">
                    <Tooltip title={!disabled ? '' : 'Permission Denied'}>
                      <MButton
                        className="block"
                        variant="contained"
                        colour={disabled ? Colours.Gray4 : Colours.Secondary}
                        type="submit"
                        disabled={disabled}
                        text="Save"
                        textSize={14}
                        textColour={Colours.White}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </Paper>
          </div>
        </div>
      </form>
    </>
  );
};

Page.propTypes = {
  selectedPage: PropTypes.shape({
    id:        PropTypes.number,
    staffType: PropTypes.string,
    plan:      PropTypes.string,
  }).isRequired,
  dispatchPageUpdate: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  selectedPage: state.page.selected,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchPageUpdate: (page) => pageAction.pageUpdate(dispatch, page),
});
export default connect(mapStateToProps, mapDispatchToProps)(Page);
